// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Deck editor runner.
 * @author James Reid
 */

// @ts-check

// @@imports-dependencies
import { presets, Deck } from "../../../../package/index.js"

// @@imports-package
import { App } from "../../app/App.js"
import {
    SteppedContainer,
    DeckToolbar,
    DeckRenderer,
    EdgeLengthEditor,
    EdgeTypeEditor
} from "../../components/index.js"
import { cloneTemplate, crossFadeElements, fadeElementIn, getDeckImageUrl, getElementByAccessor, renderLucideIcons } from "../../utils/index.js"

// @@imports-module
import { Layout } from "../Layout.js"

// @@body
const deckEditor = new Layout(async () => {
    const { container, state } = App.instance

    // If 
    if (!state.deck.client) {
        // 
        const deckOptions = Object.values(presets)
        deckOptions.sort((deckOptionA, deckOptionB) => {
            return deckOptionA.deck.edges.size - deckOptionB.deck.edges.size
        })
        state.deck.client = new Deck({
            vertices: state.deck.default.deck.copy().vertices.array,
            isClosed: true
        })
        state.deck.id = state.deck.default.id

        // 
        const options = cloneTemplate("deck-editor-shape-select")
        const standardRoofs = getElementByAccessor("standard-roofs", {
            parent: options,
            prototype: HTMLDivElement.prototype
        })
        const complexRoofs = getElementByAccessor("complex-roofs", {
            parent: options,
            prototype: HTMLDivElement.prototype
        })

        // 
        for (const deckOption of deckOptions) {
            // 
            const card = cloneTemplate("deck-editor-shape-option")
            card.dataset.deckId = deckOption.id
            if (deckOption === state.deck.default) {
                card.classList.add("selected")
            }

            // 
            getElementByAccessor("shape-name", {
                parent: card,
                prototype: HTMLHeadingElement.prototype
            }).innerText = deckOption.title
            getElementByAccessor("edge-count", {
                parent: card,
                prototype: HTMLParagraphElement.prototype
            }).innerText = `${deckOption.deck.edges.size} edges`

            // 
            deckOption.imageUrl ??= await getDeckImageUrl(deckOption)
            getElementByAccessor("deck-image", {
                parent: card,
                prototype: HTMLImageElement.prototype
            }).src = deckOption.imageUrl

            // 
            card.addEventListener("click", () => {
                // 
                state.deck.client = new Deck({
                    vertices: deckOption.deck.copy().vertices.array,
                    isClosed: true
                })
                state.deck.id = deckOption.id

                // 
                for (const card of options.querySelectorAll(".card")) {
                    const { deckId } = /** @type {HTMLElement} */ (card).dataset
                    if (deckId === state.deck.id) {
                        card.classList.add("selected")
                    }
                    else { card.classList.remove("selected") }
                }
            })

            // 
            if (deckOption.deck.edges.size < 8) { standardRoofs.append(card) }
            // deckOption.deck.edges.size < 8 ? standardRoofs.append(card)
            //     : complexRoofs.append(card)
        }

        await /** @type {Promise<void>} */ (new Promise(resolve => {
            // 
            const modalContainer = SteppedContainer.modalContainer()
            modalContainer.append(options)
            container.loadModal(modalContainer)

            // 
            getElementByAccessor("deck-shape-continue", {
                parent: options,
                prototype: HTMLButtonElement.prototype
            }).addEventListener("click", async () => {
                await container.unloadModal()
                resolve()
            })
        }))
    }

    const toolbar = new DeckToolbar()
    const renderer = new DeckRenderer()

    state.deck.renderer = renderer
    const { dimensions, types } = state.deck.client.isDefined
    if (dimensions) { renderer.renderFeatures.types = true }

    const stepProgressContent = /** @type {HTMLDivElement} */ (App
        .instance.container.progressContainer
        .querySelector("div.step-progress-content"))
    const deckEditorProgressContent = cloneTemplate("deck-editor-progress-content")

    const openToolbar = /** @type {HTMLButtonElement} */
        (deckEditorProgressContent.querySelector("button#open-toolbar"))
    const closeToolbar = /** @type {HTMLButtonElement} */
        (deckEditorProgressContent.querySelector("button#close-toolbar"))
    openToolbar.addEventListener("click", () => {
        toolbar.open()
        crossFadeElements(openToolbar, closeToolbar)
    })
    closeToolbar.addEventListener("click", () => {
        toolbar.close()
        crossFadeElements(closeToolbar, openToolbar)
    })

    stepProgressContent.append(deckEditorProgressContent)
    renderLucideIcons()
    fadeElementIn(stepProgressContent)

    const content = SteppedContainer.contentContainer()
    content.style.position = "relative" // move this to class and put change in stylesheet 
    content.style.overflow = "hidden"
    content.append(toolbar, renderer)

    await renderer.init()
    await container.load(content)

    if (!dimensions || !types) {
        const popoverContainer = SteppedContainer.popoverContainer()
        const editor = !dimensions ? new EdgeLengthEditor()
            : new EdgeTypeEditor()

        popoverContainer.append(editor)
        container.loadPopover(popoverContainer)
    }
}, {
    id: "deck-editor",
    title: "Enter Roof Details",
    name: "Details"
})

// @@exports
export { deckEditor }
